const commonTheme = {
  typography: {
    fontFamily: "'Space Grotesk', 'Inter', sans-serif",
    heading: {
      fontWeight: 700,
      h1: 'clamp(2.5rem, 5vw, 4.5rem)',
      h2: 'clamp(1.8rem, 3.5vw, 3rem)',
      h3: 'clamp(1.5rem, 3vw, 2.5rem)',
      h4: '1.5rem',
      h5: '1.25rem',
      h6: '1rem',
    },
    body: {
      fontWeight: 400,
      large: '1.125rem',
      regular: '1rem',
      small: '0.875rem',
    },
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '4rem',
    xxl: '8rem',
  },
  breakpoints: {
    mobile: '640px',
    tablet: '768px',
    laptop: '1024px',
    desktop: '1280px',
  },
  container: {
    padding: '1.5rem',
    maxWidth: '1280px',
  },
  borderRadius: {
    small: '0.375rem',
    medium: '0.75rem',
    large: '1.25rem',
    xl: '2rem',
    full: '9999px',
  },
  transitions: {
    default: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
};

const colorThemes = {
  default: {
    primary: '#6366f1',      // Cyber Indigo
    primaryHover: '#4f46e5',
    secondary: '#0ea5e9',    // Electric Cyan
    accent: '#f43f5e',       // Rose Neon
  },
  aquaGreen: {
    primary: '#0d9488',      // Deep Teal
    primaryHover: '#0f766e',
    secondary: '#10b981',    // Emerald Neon
    accent: '#f59e0b',       // Amber
  },
  coralPink: {
    primary: '#f43f5e',      // Cyber Rose
    primaryHover: '#e11d48',
    secondary: '#f97316',    // Cyber Orange
    accent: '#8b5cf6',       // Electric Violet
  },
  tealBlue: {
    primary: '#06b6d4',      // Aqua Neon
    primaryHover: '#0891b2',
    secondary: '#6366f1',    // Royal Indigo
    accent: '#d946ef',       // Electric Orchid
  },
};

export const createTheme = (variant = 'default', mode = 'light') => {
  const colors = colorThemes[variant] || colorThemes.default;

  if (mode === 'light') {
    return {
      ...commonTheme,
      colors: {
        ...colors,
        dark: '#030303',
        light: '#ffffff',
        gray: '#f5f7fa',
        textDark: '#0b0f19',
        textLight: '#ffffff',
        textGray: '#5e6b7e',
        background: '#f8fafc',
        surface: '#ffffff',
        surfaceAlt: '#f1f5f9',
        border: 'rgba(99, 102, 241, 0.08)',
        borderLight: 'rgba(0, 0, 0, 0.04)',
        gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        gradientSubtle: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.secondary}08 100%)`,
        glass: 'rgba(255, 255, 255, 0.75)',
        glassStroke: 'rgba(255, 255, 255, 0.5)',
      },
      shadows: {
        xs: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
        small: '0 4px 12px rgba(0, 0, 0, 0.04)',
        medium: '0 12px 30px rgba(0, 0, 0, 0.06)',
        large: '0 30px 60px rgba(0, 0, 0, 0.09)',
        glow: `0 0 20px ${colors.primary}18`,
        glowLg: `0 0 40px ${colors.primary}15, 0 0 80px ${colors.primary}08`,
        glowSm: `0 0 10px ${colors.primary}12`,
        card: '0 8px 30px rgba(0, 0, 0, 0.02)',
        cardHover: '0 24px 60px rgba(99, 102, 241, 0.08)',
      },
    };
  } else {
    return {
      ...commonTheme,
      colors: {
        ...colors,
        dark: '#030303',
        light: '#0b0c10',
        gray: '#12131a',
        textDark: '#f3f4f6',
        textLight: '#ffffff',
        textGray: '#8f9cae',
        background: '#030303',
        surface: '#0a0b0d',
        surfaceAlt: '#111217',
        border: 'rgba(255, 255, 255, 0.05)',
        borderLight: 'rgba(255, 255, 255, 0.02)',
        gradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
        gradientSubtle: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`,
        glass: 'rgba(10, 11, 13, 0.75)',
        glassStroke: 'rgba(255, 255, 255, 0.04)',
      },
      shadows: {
        xs: '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
        small: '0 4px 12px rgba(0, 0, 0, 0.5)',
        medium: '0 12px 30px rgba(0, 0, 0, 0.6)',
        large: '0 30px 60px rgba(0, 0, 0, 0.7)',
        glow: `0 0 20px ${colors.primary}30`,
        glowLg: `0 0 40px ${colors.primary}25, 0 0 80px ${colors.primary}12`,
        glowSm: `0 0 10px ${colors.primary}20`,
        card: '0 8px 30px rgba(0, 0, 0, 0.3)',
        cardHover: '0 24px 60px rgba(99, 102, 241, 0.15)',
      },
    };
  }
};

export const lightTheme = createTheme('default', 'light');
export const darkTheme = createTheme('default', 'dark');

export default lightTheme;